import * as THREE from 'three'

import { type FrameSettings } from '../settings'

export const frameBackBaseIndex = 0

export const frameFrontOuterBaseIndex = 4

export const frameFrontInnerBaseIndex = 8

export const mountFrontOuterBaseIndex = 12

export const mountBackBaseIndex = 16

export const mountFrontInnerBaseIndex = 20

export const artworkBaseIndex = 24

export const generateVertices = (frameSettings: FrameSettings) => {
  const artworkHalfWidth = frameSettings.artworkWidth / 2
  const artworkHalfHeight = frameSettings.artworkHeight / 2

  const frameBackDepth = 0
  const frameFrontDepth = frameBackDepth + frameSettings.frameDepth
  const frameInsetDepth = Math.max(
    frameFrontDepth - frameSettings.frameInset,
    frameBackDepth,
  )
  const mountBackDepth = Math.max(
    frameInsetDepth - frameSettings.passepartoutDepth,
    frameBackDepth,
  )
  const mountFrontDepth = mountBackDepth + frameSettings.passepartoutDepth
  const artworkDepth = mountBackDepth

  // Vertices, positioned by watching from front.
  // All vertices are laid out in quad fashion, following CSS TRBL order.
  // Names in format of <SET>.<xy>.
  return [
    // Frame back
    // 0, left top
    [
      -artworkHalfWidth -
        frameSettings.frameLeftSize -
        frameSettings.passepartoutLeftSize,
      artworkHalfHeight +
        frameSettings.frameTopSize +
        frameSettings.passepartoutTopSize,
      frameBackDepth,
    ],
    // 1, right top
    [
      artworkHalfWidth +
        frameSettings.frameRightSize +
        frameSettings.passepartoutRightSize,
      artworkHalfHeight +
        frameSettings.frameTopSize +
        frameSettings.passepartoutTopSize,
      frameBackDepth,
    ],
    // 2, right bottom
    [
      artworkHalfWidth +
        frameSettings.frameRightSize +
        frameSettings.passepartoutRightSize,
      -artworkHalfHeight -
        frameSettings.frameBottomSize -
        frameSettings.passepartoutBottomSize,
      frameBackDepth,
    ],
    // 3, left bottom
    [
      -artworkHalfWidth -
        frameSettings.frameLeftSize -
        frameSettings.passepartoutLeftSize,
      -artworkHalfHeight -
        frameSettings.frameBottomSize -
        frameSettings.passepartoutBottomSize,
      frameBackDepth,
    ],

    // Frame front (outer)
    // 4, left top
    [
      -artworkHalfWidth -
        frameSettings.frameLeftSize -
        frameSettings.passepartoutLeftSize,
      artworkHalfHeight +
        frameSettings.frameTopSize +
        frameSettings.passepartoutTopSize,
      frameFrontDepth,
    ],
    // 5, right top
    [
      artworkHalfWidth +
        frameSettings.frameRightSize +
        frameSettings.passepartoutRightSize,
      artworkHalfHeight +
        frameSettings.frameTopSize +
        frameSettings.passepartoutTopSize,
      frameFrontDepth,
    ],
    // 6, right bottom
    [
      artworkHalfWidth +
        frameSettings.frameRightSize +
        frameSettings.passepartoutRightSize,
      -artworkHalfHeight -
        frameSettings.frameBottomSize -
        frameSettings.passepartoutBottomSize,
      frameFrontDepth,
    ],
    // 7, left bottom
    [
      -artworkHalfWidth -
        frameSettings.frameLeftSize -
        frameSettings.passepartoutLeftSize,
      -artworkHalfHeight -
        frameSettings.frameBottomSize -
        frameSettings.passepartoutBottomSize,
      frameFrontDepth,
    ],

    // Frame front (inner)
    // 8, left top
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      frameFrontDepth,
    ],
    // 9, right top
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      frameFrontDepth,
    ],
    // 10, right bottom
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      frameFrontDepth,
    ],
    // 11, left bottom
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      frameFrontDepth,
    ],

    // Mount front (outer)
    // 12, left top
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      mountFrontDepth,
    ],
    // 13, right top
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      mountFrontDepth,
    ],
    // 14, right bottom
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      mountFrontDepth,
    ],
    // 15, left bottom
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      mountFrontDepth,
    ],

    // Mount back
    // 16, left top
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      mountBackDepth,
    ],
    // 17, right top
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      artworkHalfHeight + frameSettings.passepartoutTopSize,
      mountBackDepth,
    ],
    // 18, right bottom
    [
      artworkHalfWidth + frameSettings.passepartoutRightSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      mountBackDepth,
    ],
    // 19, left bottom
    [
      -artworkHalfWidth - frameSettings.passepartoutLeftSize,
      -artworkHalfHeight - frameSettings.passepartoutBottomSize,
      mountBackDepth,
    ],

    // Mount front (inner)
    // 20, left top
    [
      -artworkHalfWidth - frameSettings.passepartoutDepth,
      artworkHalfHeight + frameSettings.passepartoutDepth,
      mountFrontDepth,
    ],
    // 21, right top
    [
      artworkHalfWidth + frameSettings.passepartoutDepth,
      artworkHalfHeight + frameSettings.passepartoutDepth,
      mountFrontDepth,
    ],
    // 22, right bottom
    [
      artworkHalfWidth + frameSettings.passepartoutDepth,
      -artworkHalfHeight - frameSettings.passepartoutDepth,
      mountFrontDepth,
    ],
    // 23, left bottom
    [
      -artworkHalfWidth - frameSettings.passepartoutDepth,
      -artworkHalfHeight - frameSettings.passepartoutDepth,
      mountFrontDepth,
    ],

    // Artwork
    // 24, left top
    [-artworkHalfWidth, artworkHalfHeight, artworkDepth],
    // 25, right top
    [artworkHalfWidth, artworkHalfHeight, artworkDepth],
    // 26, right bottom
    [artworkHalfWidth, -artworkHalfHeight, artworkDepth],
    // 27, left bottom
    [-artworkHalfWidth, -artworkHalfHeight, artworkDepth],
  ].map((vector) => new THREE.Vector3(...vector))
}
